// Paper & Surface panel for the Canvas side dock. The surface belongs to the
// document, so this panel is available whatever tool is selected. It reads
// and changes the document surface through the physical paint system
// (vx-paint-surface-query / -set events); changing paper never touches paint.

import React, { useEffect, useState } from 'react';
import { SURFACE_LIBRARY, SURFACE_PICKER_ORDER, DEFAULT_SURFACE_SETTINGS } from '../surfaces/SurfaceLibrary.js';

const labelStyle = { fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' };
const chip = (active) => ({
  minHeight: 29,
  borderRadius: 8,
  border: active ? '1px solid rgba(96,165,250,0.70)' : '1px solid rgba(255,255,255,0.12)',
  background: active ? 'rgba(59,130,246,0.22)' : 'rgba(255,255,255,0.055)',
  color: 'rgba(245,247,250,0.92)',
  fontSize: 10,
  fontWeight: 900,
  cursor: 'pointer',
  padding: '0 8px',
});

function SelectRow({ label, value, options, onChange }) {
  return (
    <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{ width: '100%', minHeight: 34, borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.24)', color: 'rgba(255,255,255,0.92)', padding: '0 10px', fontSize: 12, fontWeight: 850, outline: 'none' }}
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

function SliderRow({ label, value, min, max, suffix = '', onChange }) {
  return (
    <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <span style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}</span>
        <span style={{ color: 'rgba(255,255,255,0.86)' }}>{value}{suffix}</span>
      </span>
      <input type="range" min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} style={{ width: '100%', accentColor: '#60a5fa' }} />
    </label>
  );
}

const PAPER_COLOURS = [
  { id: null, label: 'Preset' },
  { id: '#fbfaf6', label: 'White' },
  { id: '#f3ead6', label: 'Cream' },
  { id: '#efe4cc', label: 'Ivory' },
];

export function PaperIcon({ size = 14 }) {
  // Stacked sheets with a grain line: "paper & surface".
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ flex: 'none' }}>
      <path d="M4 2.5h7.5l2 2V12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.55" />
      <rect x="2.5" y="4.5" width="9" height="9.5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4.5 8c1.3-.8 2.5.8 3.8 0s2-.6 2.2-.3M4.5 10.8c1.3-.8 2.5.8 3.8 0" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
    </svg>
  );
}

export default function PaperSurfacePanel({ available = true, advancedRequest = 0 }) {
  const [surface, setSurface] = useState({ ...DEFAULT_SURFACE_SETTINGS });
  const [advanced, setAdvanced] = useState(Boolean(advancedRequest));

  // File > Paper & Surface > Custom Surface… opens the advanced controls.
  useEffect(() => {
    if (advancedRequest) setAdvanced(true);
  }, [advancedRequest]);

  useEffect(() => {
    const onSurface = (event) => { if (event && event.detail) setSurface((s) => ({ ...s, ...event.detail })); };
    window.addEventListener('vx-paint-surface', onSurface);
    window.dispatchEvent(new CustomEvent('vx-paint-surface-query'));
    return () => window.removeEventListener('vx-paint-surface', onSurface);
  }, []);

  if (!available) {
    return (
      <div style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,0.6)' }}>
        Paper & Surface needs the physical paint engine (WebGPU). This document uses plain digital paper.
      </div>
    );
  }

  const update = (patch) => {
    setSurface((current) => {
      const next = { ...current, ...patch };
      window.dispatchEvent(new CustomEvent('vx-paint-surface-set', { detail: next }));
      return next;
    });
  };
  const pct = (v) => Math.round(v * 100);

  return (
    <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
      <SelectRow
        label="Paper / canvas"
        value={surface.preset}
        options={SURFACE_PICKER_ORDER.map((id) => ({ id, label: SURFACE_LIBRARY[id].name }))}
        onChange={(v) => update({ preset: v })}
      />
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={labelStyle}>Paper colour</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
          {PAPER_COLOURS.map((c) => (
            <button key={c.label} type="button" style={chip(surface.paperColor === c.id)} onClick={() => update({ paperColor: c.id })}>{c.label}</button>
          ))}
          <input
            type="color"
            aria-label="Custom paper colour"
            value={surface.paperColor || '#f4efe4'}
            onChange={(event) => update({ paperColor: event.target.value })}
            style={{ width: 34, height: 28, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 7, background: 'none', padding: 2 }}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={labelStyle}>Surface behaviour</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <button type="button" style={chip(surface.mode === 'physical')} onClick={() => update({ mode: 'physical' })}>Physical surface</button>
          <button type="button" style={chip(surface.mode === 'visual')} onClick={() => update({ mode: 'visual' })}>Visual texture only</button>
        </div>
      </div>
      {surface.mode === 'physical' ? (
        <SliderRow label="Paper influence" value={pct(surface.physicalStrength)} min={0} max={100} suffix="%" onChange={(v) => update({ physicalStrength: v / 100 })} />
      ) : null}
      <SliderRow label="Paper relief" value={pct(surface.relief)} min={0} max={200} suffix="%" onChange={(v) => update({ relief: v / 100 })} />
      <button type="button" style={{ ...chip(advanced), justifySelf: 'start' }} onClick={() => setAdvanced((a) => !a)} aria-expanded={advanced}>
        {advanced ? 'Hide advanced settings' : 'Advanced settings'}
      </button>
      {advanced ? (
        <div style={{ display: 'grid', gap: 9 }}>
          <SliderRow label="Texture visibility" value={pct(surface.visibility)} min={0} max={100} suffix="%" onChange={(v) => update({ visibility: v / 100 })} />
          <SliderRow label="Texture scale" value={pct(surface.textureScale)} min={25} max={400} suffix="%" onChange={(v) => update({ textureScale: v / 100 })} />
          <SliderRow label="Texture rotation" value={Math.round(surface.textureRotation)} min={-180} max={180} suffix="°" onChange={(v) => update({ textureRotation: v })} />
          <SliderRow label="Grain depth" value={pct(surface.grainDepth)} min={0} max={150} suffix="%" onChange={(v) => update({ grainDepth: v / 100 })} />
          <SliderRow label="Absorbency" value={pct(surface.absorbencyScale)} min={0} max={300} suffix="%" onChange={(v) => update({ absorbencyScale: v / 100 })} />
          <SliderRow label="Pigment granulation" value={pct(surface.granulationScale)} min={0} max={300} suffix="%" onChange={(v) => update({ granulationScale: v / 100 })} />
          <SliderRow label="Water spread" value={pct(surface.waterSpreadScale)} min={0} max={300} suffix="%" onChange={(v) => update({ waterSpreadScale: v / 100 })} />
          <SliderRow label="Drying influence" value={pct(surface.dryingScale)} min={10} max={500} suffix="%" onChange={(v) => update({ dryingScale: v / 100 })} />
          <SliderRow label="Brush friction" value={pct(surface.frictionScale)} min={0} max={300} suffix="%" onChange={(v) => update({ frictionScale: v / 100 })} />
          <SliderRow label="Surface roughness" value={pct(surface.roughnessScale)} min={30} max={200} suffix="%" onChange={(v) => update({ roughnessScale: v / 100 })} />
        </div>
      ) : null}
      <div style={{ fontSize: 10, lineHeight: 1.45, color: 'rgba(255,255,255,0.5)' }}>
        Paper belongs to the whole document. Changing it keeps your painting; the new surface affects paint from now on.
      </div>
    </div>
  );
}
